'use client';

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { createClient } from '@/lib/supabase/client';

export type UILocale = 'en' | 'fr' | 'id' | 'tl' | 'es' | 'de' | 'zh' | 'hi';

interface LanguageContextValue {
  locale: UILocale;
  setLocale: (locale: UILocale) => void;
  isRTL: boolean;
}

const LanguageContext = createContext<LanguageContextValue>({
  locale: 'en',
  setLocale: () => {},
  isRTL: false,
});

const STORAGE_KEY = 'ertzyx_ui_locale';
const COOKIE_KEY = 'ertzyx_locale';

// Locales that use RTL layout (preparation for Arabic)
const RTL_LOCALES: UILocale[] = [];

export const LOCALE_LABELS: Record<UILocale, string> = {
  en: 'English',
  fr: 'Francais',
  id: 'Bahasa Indonesia',
  tl: 'Filipino',
  es: 'Espanol',
  de: 'Deutsch',
  zh: '中文',
  hi: 'हिन्दी',
};

export const LOCALE_NATIVE_NAMES: Record<UILocale, string> = {
  en: 'English',
  fr: 'Francais',
  id: 'Bahasa Indonesia',
  tl: 'Filipino',
  es: 'Espanol',
  de: 'Deutsch',
  zh: '中文（简体）',
  hi: 'हिन्दी',
};

export const LOCALE_SHORT: Record<UILocale, string> = {
  en: 'EN',
  fr: 'FR',
  id: 'ID',
  tl: 'FIL',
  es: 'ES',
  de: 'DE',
  zh: '中文',
  hi: 'हि',
};

// Only locales with complete-enough dictionary coverage are exposed to users (selector,
// auto-detection, and the ?lang= campaign override all gate through this list). 'de', 'zh',
// and 'es' reached full key parity with en.json across every active-UI namespace (public
// nav/footer, hero, product, Memory Lab, For Institutions, public Trust Ledger, register,
// both dashboard sidebars/topbars, My Circles, Program Verification, common/status labels)
// and were re-exposed accordingly. 'hi' and 'tl' still have only partial coverage of those
// namespaces — they stay hidden until completed, per the product rule "do not leave visible
// language options that only partially translate the app." Re-add a code here once its
// dictionary reaches the same parity to expose it again.
export const AVAILABLE_LOCALES: UILocale[] = ['en', 'fr', 'id', 'de', 'zh', 'es'];

// Full set of locales that have a dictionary file, even if not currently exposed in the
// selector/detection. Used only for internal bookkeeping — never for user-facing gating.
export const ALL_KNOWN_LOCALES: UILocale[] = ['en', 'es', 'de', 'zh', 'hi', 'fr', 'id', 'tl'];

// BCP-47 tags for each locale (used in html lang and Accept-Language matching)
export const LOCALE_BCP47: Record<UILocale, string> = {
  en: 'en',
  fr: 'fr',
  id: 'id',
  tl: 'tl',
  es: 'es',
  de: 'de',
  zh: 'zh-Hans',
  hi: 'hi',
};

// Full readable name for each locale in that locale's language (used in hreflang / SEO)
export const LOCALE_HREFLANG: Record<UILocale, string> = {
  en: 'en',
  fr: 'fr',
  id: 'id',
  tl: 'tl',
  es: 'es',
  de: 'de',
  zh: 'zh-Hans',
  hi: 'hi',
};

function setCookie(name: string, value: string, days = 365) {
  if (typeof document === 'undefined') return;
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires};path=/;SameSite=Lax`;
}

function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

function normalizeLocaleTag(base: string): UILocale | null {
  if (AVAILABLE_LOCALES.includes(base as UILocale)) return base as UILocale;
  // Map Filipino/Tagalog browser codes
  if (base === 'fil' || base === 'tl') return AVAILABLE_LOCALES.includes('tl') ? 'tl' : null;
  // Map Chinese variants to zh
  if (base === 'zh') return AVAILABLE_LOCALES.includes('zh') ? 'zh' : null;
  return null;
}

/**
 * Map a BCP-47 browser language tag (e.g. "fr-CA", "id", "en-US") to a supported
 * UI locale. Used as a non-destructive default — an explicit user choice always takes precedence.
 * Exported so other detection surfaces (e.g. LanguageSuggestionBanner) share one implementation
 * instead of maintaining a parallel copy.
 */
export function detectBrowserLocale(): UILocale | null {
  if (typeof navigator === 'undefined') return null;
  const candidates = [navigator.language, ...(navigator.languages || [])].filter(Boolean);
  for (const tag of candidates) {
    const match = normalizeLocaleTag(tag.toLowerCase().split('-')[0]);
    if (match) return match;
  }
  return null;
}

// Coarse, non-invasive region signal derived from the browser's IANA time zone — no
// geolocation permission, no network call, no blocking. Only used as a fallback when
// navigator.language/navigator.languages did not resolve to a supported locale. Each
// mapped locale still passes through AVAILABLE_LOCALES before being used, so hidden
// locales here simply fall through to the next step (English) until they're exposed.
const TIMEZONE_LOCALE_MAP: Record<string, UILocale> = {
  // Indonesia
  'Asia/Jakarta': 'id',
  'Asia/Makassar': 'id',
  'Asia/Jayapura': 'id',
  'Asia/Pontianak': 'id',
  // France
  'Europe/Paris': 'fr',
  // Quebec — the one Canadian region where a French default is a safe assumption;
  // the rest of Canada intentionally defers to browser language (see detectBrowserLocale).
  'America/Montreal': 'fr',
  // Spain / Latin America
  'Europe/Madrid': 'es',
  'America/Mexico_City': 'es',
  'America/Bogota': 'es',
  'America/Buenos_Aires': 'es',
  'America/Santiago': 'es',
  'America/Lima': 'es',
  // Germany, Austria, Switzerland
  'Europe/Berlin': 'de',
  'Europe/Vienna': 'de',
  'Europe/Zurich': 'de',
  // Philippines
  'Asia/Manila': 'tl',
  // China
  'Asia/Shanghai': 'zh',
  'Asia/Chongqing': 'zh',
  'Asia/Urumqi': 'zh',
  // India
  'Asia/Kolkata': 'hi',
  'Asia/Calcutta': 'hi',
};

function detectRegionLocale(): UILocale | null {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const mapped = tz ? TIMEZONE_LOCALE_MAP[tz] : undefined;
    return mapped && AVAILABLE_LOCALES.includes(mapped) ? mapped : null;
  } catch {
    return null;
  }
}

function readUrlLangParam(): UILocale | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = new URLSearchParams(window.location.search).get('lang');
    if (!raw) return null;
    return normalizeLocaleTag(raw.toLowerCase());
  } catch {
    return null;
  }
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<UILocale>('en');

  const persistLocale = useCallback((next: UILocale) => {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // localStorage unavailable (private browsing etc.)
    }
    setCookie(COOKIE_KEY, next);
  }, []);

  useEffect(() => {
    // Priority order for UI language (first match wins):
    //   1. ?lang= URL parameter (campaign/ad links) — always wins, even over a previously
    //      saved preference, and is itself persisted so it becomes the new saved preference.
    //   2. Explicit stored user preference — localStorage, then cookie.
    //   3. Browser language (navigator.language / navigator.languages).
    //   4. Coarse region fallback derived from IANA time zone — only consulted when browser
    //      language didn't resolve to a supported locale.
    //   5. English.
    // Every candidate is validated against AVAILABLE_LOCALES, so an unsupported/invalid value
    // at any step (e.g. ?lang=xx, or a hidden locale) simply falls through to the next step.
    const fromUrl = readUrlLangParam();
    if (fromUrl) {
      setLocaleState(fromUrl);
      persistLocale(fromUrl);
      return;
    }

    try {
      const stored = localStorage.getItem(STORAGE_KEY) as UILocale | null;
      if (stored && AVAILABLE_LOCALES.includes(stored)) {
        setLocaleState(stored);
        setCookie(COOKIE_KEY, stored);
        return;
      }
    } catch {
      // localStorage unavailable
    }

    const fromCookie = getCookie(COOKIE_KEY) as UILocale | null;
    if (fromCookie && AVAILABLE_LOCALES.includes(fromCookie)) {
      setLocaleState(fromCookie);
      return;
    }

    const browserLocale = detectBrowserLocale();
    if (browserLocale) {
      setLocaleState(browserLocale);
      persistLocale(browserLocale);
      return;
    }

    const regionLocale = detectRegionLocale();
    if (regionLocale) {
      setLocaleState(regionLocale);
      persistLocale(regionLocale);
    }
    // Otherwise the 'en' initial state stands — no need to persist the default; a future
    // visit is free to re-run detection (e.g. after the browser's language changes).
  }, [persistLocale]);

  const setLocale = useCallback(
    (next: UILocale) => {
      setLocaleState(next);
      persistLocale(next);
      // Update html lang attribute for accessibility + RTL
      if (typeof document !== 'undefined') {
        document.documentElement.lang = LOCALE_BCP47[next];
        document.documentElement.dir = RTL_LOCALES.includes(next) ? 'rtl' : 'ltr';
      }
      // Best-effort: only reachable when the user explicitly picks a language
      // (never on the auto-detect path above), and only takes effect when
      // signed in. This is the one server-readable source of a user's
      // language preference, used to localize push notification content
      // server-side.
      try {
        const supabase = createClient();
        void supabase.auth
          .getUser()
          .then(async ({ data: { user } }) => {
            if (!user) return;
            await supabase
              .from('user_language_preferences')
              .upsert({ user_id: user.id, preferred_language: next }, { onConflict: 'user_id' });
          })
          .catch(() => {});
      } catch {
        // ignore
      }
    },
    [persistLocale]
  );

  const isRTL = RTL_LOCALES.includes(locale);

  return (
    <LanguageContext.Provider value={{ locale, setLocale, isRTL }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage(): LanguageContextValue {
  return useContext(LanguageContext);
}
